// ============================================================================
// Finance view — pure arithmetic and presentation rules (CW-Q).
//
// Everything here is a pure function so the rules Jose wrote down are testable
// instead of scattered through JSX. The rules that are NOT cosmetic and must
// not be "improved on":
//
//   * Margin bands come from the DATABASE (app_settings -> v_finance_thresholds).
//     There is no threshold literal in this module or any finance component.
//   * Bullet charts run a 20–34% scale, labelled. Margins live in a 14-point
//     window; a zero-based axis wastes two thirds of the width.
//   * `$/yr to reach target` reveals its arithmetic, and is the SIZE OF THE FIX
//     LIST, not a loss. It is never netted against accounts above target.
//   * ARR is a run rate, not contracted backlog. Say so wherever it appears.
//   * A building with no recorded cost is never 100% margin.
// ============================================================================

export type Territory =
  | 'Kansas City & Midwest'
  | 'Colorado'
  | 'North Texas'
  | 'South Texas'
  | 'Nevada'
  | 'Florida';

export type ServiceLine = 'Hospitals' | 'Clinics' | 'Dialysis';

export type MarginBand = 'green' | 'amber' | 'red' | 'unknown';

/** Bryan's bands, read from the DB. Never construct one from a literal. */
export interface MarginThresholds {
  target_pct: number;
  amber_floor_pct: number;
}

// ----- Territory identity ----------------------------------------------------
// Colour is TERRITORY, not margin (Jose's spec). The CSS variable is the single
// colour authority; the classes are the Tailwind families registered against
// those variables in tailwind.config.js.

export interface TerritoryStyle {
  cssVar: string;
  swatchClass: string;
  borderClass: string;
}

export const TERRITORY_STYLES: Record<Territory, TerritoryStyle> = {
  Florida: {
    cssVar: '--terr-fl',
    swatchClass: 'bg-terr-fl',
    borderClass: 'border-terr-fl',
  },
  Nevada: {
    cssVar: '--terr-nv',
    swatchClass: 'bg-terr-nv',
    borderClass: 'border-terr-nv',
  },
  'Kansas City & Midwest': {
    cssVar: '--terr-kc',
    swatchClass: 'bg-terr-kc',
    borderClass: 'border-terr-kc',
  },
  Colorado: {
    cssVar: '--terr-co',
    swatchClass: 'bg-terr-co',
    borderClass: 'border-terr-co',
  },
  'South Texas': {
    cssVar: '--terr-stx',
    swatchClass: 'bg-terr-stx',
    borderClass: 'border-terr-stx',
  },
  'North Texas': {
    cssVar: '--terr-ntx',
    swatchClass: 'bg-terr-ntx',
    borderClass: 'border-terr-ntx',
  },
};

/** Display order on the page: biggest book first, matching Jose's §4. */
export const TERRITORY_ORDER: Territory[] = [
  'Kansas City & Midwest',
  'Colorado',
  'South Texas',
  'North Texas',
  // Nevada last: it is the smallest book, and today it carries no billed revenue
  // at all. It is on the page because the RELATIONSHIP is real, not because the
  // dollars are (Ben, 2026-08-20).
  'Nevada',
  // Florida is NOT the Midwest. It was folded into Kansas City & Midwest until
  // 2026-08-20, so the only Florida building we have was invisible as Florida
  // from the day we opened it -- which is when a new market most needs watching
  // (Ben). Small is the reason to show it, not the reason to hide it.
  'Florida',
];

/**
 * Territory colour names, spelled out for the map legend. Jose named these
 * explicitly and they are load-bearing: the map is read alongside a printed
 * deck where the reader has to match a shape to a row.
 */
export const TERRITORY_COLOUR_NAME: Record<Territory, string> = {
  'Kansas City & Midwest': 'gold',
  Colorado: 'blue',
  'South Texas': 'purple',
  'North Texas': 'green',
  Nevada: 'crimson',
  Florida: 'teal',
};

/**
 * States each territory covers. Omaha and St. Louis are dashed into Kansas City
 * on the page (Jose's §3 note) — separate `market_region` values, one territory.
 * Orlando was in that list until 2026-08-20; Florida is its own territory now,
 * because Florida is not the Midwest.
 */
export const TERRITORY_STATES: Record<Territory, string[]> = {
  'Kansas City & Midwest': ['Kansas', 'Missouri', 'Nebraska'],
  Colorado: ['Colorado'],
  'South Texas': ['Texas — Houston, Austin, San Antonio'],
  'North Texas': ['Texas — Dallas–Fort Worth'],
  Nevada: ['Nevada'],
  Florida: ['Florida'],
};

export function isTerritory(v: string | null | undefined): v is Territory {
  return (
    v === 'Kansas City & Midwest' ||
    v === 'Colorado' ||
    v === 'North Texas' ||
    v === 'South Texas' ||
    v === 'Nevada' ||
    v === 'Florida'
  );
}

// ----- Service line ----------------------------------------------------------
// On-screen words are "Hospitals / Clinics / Dialysis" — NEVER the raw enum.
// The DB view owns the CASE so the taxonomy re-spine (Surgery_Center ->
// clinic_surgical / clinic_nonsurgical, MOB as a site attribute) is a one-place
// edit there; this order is the only thing the client contributes.

export const SERVICE_LINE_ORDER: ServiceLine[] = [
  'Hospitals',
  'Clinics',
  'Dialysis',
];

// ----- Margin bands ----------------------------------------------------------

/**
 * Classify a margin against Bryan's bands. A null margin — a slice whose
 * revenue carries no recorded cost — is 'unknown', never green: the whole point
 * of §12 is that a missing cost must not read as a perfect margin.
 */
export function marginBand(
  marginPctValue: number | null | undefined,
  t: MarginThresholds,
): MarginBand {
  if (
    marginPctValue === null ||
    marginPctValue === undefined ||
    !Number.isFinite(marginPctValue)
  ) {
    return 'unknown';
  }
  if (marginPctValue >= t.target_pct) return 'green';
  if (marginPctValue >= t.amber_floor_pct) return 'amber';
  return 'red';
}

/** Status-token classes per band. Text uses the AA-safe `-fg` channels. */
export const BAND_CLASSES: Record<
  MarginBand,
  { text: string; bg: string; border: string }
> = {
  green: { text: 'text-st-ok-fg', bg: 'bg-st-ok-bg', border: 'border-st-ok-border' },
  amber: {
    text: 'text-st-warn-fg',
    bg: 'bg-st-warn-bg',
    border: 'border-st-warn-border',
  },
  red: {
    text: 'text-st-danger-fg',
    bg: 'bg-st-danger-bg',
    border: 'border-st-danger-border',
  },
  unknown: {
    text: 'text-tcs-text-muted',
    bg: 'bg-tcs-bg-muted',
    border: 'border-tcs-border',
  },
};

/** Human label for a band, with the live numbers in it. */
export function bandLabel(band: MarginBand, t: MarginThresholds): string {
  switch (band) {
    case 'green':
      return `At or above target (${fmtPct(t.target_pct)})`;
    case 'amber':
      return `Below target, at or above ${fmtPct(t.amber_floor_pct)}`;
    case 'red':
      return `Below ${fmtPct(t.amber_floor_pct)}`;
    default:
      return 'No recorded cost — margin unknown';
  }
}

// ----- Margin arithmetic -----------------------------------------------------

export interface Slice {
  revenue: number;
  sub_cost: number;
  has_recorded_cost?: boolean;
  /** See FinanceRow.revenue_basis. Absent is treated as 'tracked'. */
  revenue_basis?: 'tracked' | 'billed_under_parent' | 'no_separate_revenue';
}

/**
 * (revenue − sub cost) ÷ revenue, as a percentage. Returns null when revenue is
 * zero or absent — a zero denominator is not 0% margin, it is no margin.
 */
export function marginPct(revenue: number, subCost: number): number | null {
  if (!Number.isFinite(revenue) || revenue <= 0) return null;
  return ((revenue - subCost) / revenue) * 100;
}

export interface RollUp {
  buildings: number;
  /**
   * Buildings that carry a revenue figure. `buildings` counts every building we
   * serve; this counts the ones with a number against them. They differ because a
   * hospital stays in the book on the strength of the RELATIONSHIP, not the
   * current service line (Ben, 2026-08-20).
   */
  revenueBearing: number;
  /** Buildings carrying no billed revenue at all -- distinct from a RATED building with no cost. */
  noRevenueBuildings: number;
  revenue: number;
  subCost: number;
  marginPct: number | null;
  arr: number;
  /** Buildings inside this roll-up with no recorded cost at all. */
  noCostBuildings: number;
  /** Revenue those buildings carry — the part of the margin that is a guess. */
  noCostRevenue: number;
  /** Margin over ONLY the revenue that has a cost against it. */
  coveredMarginPct: number | null;
}

/**
 * Roll a set of buildings up. Two margins come out on purpose:
 *   `marginPct`        — Jose's §1 arithmetic, the tie-out figure Page 1 and
 *                        Page 2 must agree on to the cent.
 *   `coveredMarginPct` — the same sum over revenue that HAS a cost against it.
 * Showing both is how "never count a building with no recorded cost as 100%
 * margin" is satisfied without silently moving the number Bryan signed off.
 */
export function rollUp(slices: Slice[]): RollUp {
  let revenue = 0;
  let subCost = 0;
  let noCostBuildings = 0;
  let noCostRevenue = 0;
  let coveredRevenue = 0;
  let coveredCost = 0;
  let revenueBearing = 0;
  let noRevenueBuildings = 0;

  for (const s of slices) {
    const r = Number(s.revenue) || 0;
    const c = Number(s.sub_cost) || 0;
    if ((s.revenue_basis ?? 'tracked') === 'tracked') revenueBearing += 1;
    revenue += r;
    subCost += c;
    if (s.has_recorded_cost !== false) {
      coveredRevenue += r;
      coveredCost += c;
    } else if ((s.revenue_basis ?? 'tracked') === 'tracked') {
      // A RATED building with nobody assigned to it. This is a data fault: it
      // would read as 100% margin.
      noCostBuildings += 1;
      noCostRevenue += r;
    } else {
      // A hospital we serve that has nothing separately billed this period, or
      // whose billing sits on its parent. Not a fault, and NOT a rated building
      // with a missing cost -- counting it as one overstated the fault list by
      // five the moment #346 landed.
      noRevenueBuildings += 1;
    }
  }

  return {
    buildings: slices.length,
    revenueBearing,
    noRevenueBuildings,
    revenue,
    subCost,
    marginPct: marginPct(revenue, subCost),
    arr: revenue * 12,
    noCostBuildings,
    noCostRevenue,
    coveredMarginPct: marginPct(coveredRevenue, coveredCost),
  };
}

/** Group rows by a key, preserving a caller-supplied display order. */
export function groupBy<T>(
  rows: T[],
  key: (row: T) => string,
): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    const bucket = out.get(k);
    if (bucket) bucket.push(row);
    else out.set(k, [row]);
  }
  return out;
}

// ----- $/yr to reach target --------------------------------------------------

export interface ToTarget {
  amount: number;
  /** The arithmetic, spelled out, for the hover. */
  workings: string;
}

/**
 * monthly revenue × 12 × (target − margin) ÷ 100, and ONLY when the margin is
 * below target. Above target it returns null — this figure is the size of the
 * fix list, not a loss, so a surplus must never be netted against a deficit.
 */
export function dollarsPerYearToTarget(
  monthlyRevenue: number,
  marginPctValue: number | null,
  t: MarginThresholds,
): ToTarget | null {
  if (marginPctValue === null || !Number.isFinite(marginPctValue)) return null;
  if (marginPctValue >= t.target_pct) return null;
  if (!Number.isFinite(monthlyRevenue) || monthlyRevenue <= 0) return null;
  const gapPp = t.target_pct - marginPctValue;
  const amount = (monthlyRevenue * 12 * gapPp) / 100;
  return {
    amount,
    workings:
      `${fmtMoney(monthlyRevenue)}/mo × 12 × (${fmtPct(t.target_pct)} target − ` +
      `${fmtPct(marginPctValue)} actual) = ${fmtMoney(amount)}/yr. ` +
      'This is the size of the fix list, not a loss, and it is never netted ' +
      'against accounts already at or above target.',
  };
}

/** Sum a fix list. Only below-target rows contribute, by construction above. */
export function sumToTarget(items: (ToTarget | null)[]): number {
  return items.reduce((acc, i) => acc + (i ? i.amount : 0), 0);
}

// ----- Bullet chart scale ----------------------------------------------------

/** Jose's labelled window. A zero-based axis wastes two thirds of the width. */
export const BULLET_SCALE_MIN = 20;
export const BULLET_SCALE_MAX = 34;

/**
 * Position on the 20–34% axis as a 0–100 percentage of the track width.
 * Values outside the window clamp to the ends; the out-of-scale flags let the
 * caller mark that the bar is pinned rather than measured there.
 */
export function bulletPosition(marginPctValue: number | null): {
  pct: number;
  outOfScaleLow: boolean;
  outOfScaleHigh: boolean;
} {
  if (marginPctValue === null || !Number.isFinite(marginPctValue)) {
    return { pct: 0, outOfScaleLow: false, outOfScaleHigh: false };
  }
  const span = BULLET_SCALE_MAX - BULLET_SCALE_MIN;
  const raw = ((marginPctValue - BULLET_SCALE_MIN) / span) * 100;
  return {
    pct: Math.max(0, Math.min(100, raw)),
    outOfScaleLow: marginPctValue < BULLET_SCALE_MIN,
    outOfScaleHigh: marginPctValue > BULLET_SCALE_MAX,
  };
}

export const BULLET_SCALE_LABEL =
  `Scale ${BULLET_SCALE_MIN}–${BULLET_SCALE_MAX}%, not zero-based — ` +
  'labour margins live in a 14-point window.';

// ----- Formatting ------------------------------------------------------------

export function fmtMoney(
  n: number | null | undefined,
  opts?: { cents?: boolean },
): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: opts?.cents ? 2 : 0,
    maximumFractionDigits: opts?.cents ? 2 : 0,
  });
}

/** Compact dollars for tiles: $10.34m, $862k. Never where cents must tie. */
export function fmtMoneyCompact(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}m`;
  if (abs >= 1_000) return `$${Math.round(n / 1_000).toLocaleString('en-US')}k`;
  return fmtMoney(n);
}

export function fmtPct(n: number | null | undefined, dp = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `${n.toFixed(dp)}%`;
}

/** Signed percentage-point delta against target: "+1.6 pp", "−2.3 pp". */
export function fmtPp(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}${Math.abs(n).toFixed(1)} pp`;
}

/** Name the month everywhere — header, first band title, footer. */
export function monthLabel(iso: string): string {
  const d = new Date(`${iso.slice(0, 7)}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Named managers, never "2 AMs". Jose's rule, and the reason it is a rule: a
 * count is not actionable and a name is.
 */
export function namedManagers(names: (string | null | undefined)[]): string {
  const clean = Array.from(
    new Set(names.map((n) => (n && n.trim() ? n.trim() : 'Unassigned'))),
  );
  if (clean.length === 0) return 'Unassigned';
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}

/** The one sentence that must appear wherever the board sees "ARR". */
export const ARR_CAVEAT =
  'ARR is the current monthly book × 12 — a run rate, not contracted backlog.';

/**
 * Building-level dollars render a PALER red than account-level, because
 * building figures do not sum to the account figure (an account nets its
 * buildings before comparing to target). Two shades, one rule, so the two can
 * never be confused.
 */
export const ACCOUNT_LEVEL_MONEY_CLASS = 'text-st-danger-fg-strong';
export const BUILDING_LEVEL_MONEY_CLASS = 'text-st-danger-fg';
export const LEVEL_MIX_NOTE =
  'Building figures do not sum to the account figure — an account nets its ' +
  'buildings before comparing to target. Building dollars are shown a paler ' +
  'red than account dollars.';

export const MANAGER_VIEW_NOTE =
  'The manager view totals more than the account view: slices that net out ' +
  'inside a shared account do not net out across two managers. The difference ' +
  'is real and is shown, not hidden.';
